<template>
  <div v-cloak>
    <transition name="fade" appear appear-active-class="fade-enter-active">
      <div class="wrap clientindex">
        <div class="monitor-header">
          <h1>Показатели деятельности органов службы занятости</h1>
        </div>
        <div class="monitor-filter">
          <div class="d-flex flex-column flex-grow-1">
            <RegionAndDepartmentPicker
                class="mb-1"
                v-model="regionAndDepartmentInfo"
                :regions="regions"
                :loading="loading"
                nullable-department="true"
            ></RegionAndDepartmentPicker>
            <ModernizationWave :count="regionAndDepartmentInfo?.region?.modernizationWave" />
          </div>
          <div class="passport-buttons">
            <v-dialog
                v-model="dialog"
                max-width="800px"
                persistent
            >
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <v-btn outlined>
                    документы
                  </v-btn>
                </div>
              </template>
              <PassportInfoDocsDialog
                  :region="regionAndDepartmentInfo.region"
                  :departments="regionAndDepartmentInfo.region?.departments"
                  :edit-roles="[ROLES.ROLE_CZN_MANAGER, ROLES.ROLE_REGIONAL_AGENCY, ROLES.ROLE_VNII, ROLES.ROLE_ADMIN]"
                  section="Мониторинг деятельности"
                  @close="dialog=false; dialogKey++" :key="dialogKey"
                  @edit="dialog=false; dialogUpload=true;"
              />
            </v-dialog>
            <v-dialog
                v-model="dialogUpload"
                max-width="800px"
                persistent>
              <PassportInfoDocsUploadDialog
                  :region="regionAndDepartmentInfo.region"
                  :departments="regionAndDepartmentInfo.region?.departments"
                  section="Мониторинг деятельности"
                  @close="dialogUpload=false; dialogKey++"
                  :key="dialogKey"
              />
            </v-dialog>
          </div>
        </div>

        <div class="monitor-toolbar" v-if="period && !loading">
          <v-select
              class="monitor-toolbar__period"
              :items="periods"
              v-model="period"
              label="Период мониторинга"
              item-text="name"
              return-object
              dense
          >
            <template v-slot:selection="{ item }">
              <div>
                <div>{{ item.name }}</div>
                <div :class="periodStatusClass(item.status)">
                  {{ periodStatusText(item.status) }}
                </div>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div>
                <div>{{ item.name }}</div>
                <div :class="periodStatusClass(item.status)">
                  {{ periodStatusText(item.status) }}
                </div>
              </div>
            </template>
          </v-select>

          <div class="monitor-toolbar__planned">
            <v-btn
                v-if="showRegionPlannedButton"
                outlined
                @click="openPlannedDialog('region')"
            >
              Плановые показатели по регионам
            </v-btn>
            <v-btn
                v-if="showCznPlannedButton"
                outlined
                @click="openPlannedDialog('czn')"
                :disabled="!regionAndDepartmentInfo?.region"
            >
              Плановые показатели по ЦЗН
            </v-btn>
          </div>
        </div>

        <ActivityMonitorPlannedDialog
            v-if="period && !loading"
            v-model="plannedDialogOpen"
            :mode="plannedDialogMode"
            :period="period"
            :config-indicators="configIndicators"
            :regions="regions"
            :region-and-department-info="regionAndDepartmentInfo"
        />

        <v-tabs v-if="period && !loading">
          <v-tab @click="checkListKey++; $forceUpdate()">Сведения для расчета показателей</v-tab>
          <v-tab @click="reportsKey++; $forceUpdate()">Отчёты</v-tab>
          <v-tab @click="ratingKey++; $forceUpdate()">Рейтинги</v-tab>
          <v-tab-item>
            <ActivityMonitorCheckList
                :region-and-department-info="regionAndDepartmentInfo"
                :key="checkListKey"
                :period="period"
                :config-checklist="configChecklist"
                :config-files="configFiles"
                :config-indicators="configIndicators"
                :regions="regions"
            ></ActivityMonitorCheckList>
          </v-tab-item>
          <v-tab-item>
            <ActivityMonitorReports
                :region-and-department-info="regionAndDepartmentInfo"
                :key="reportsKey"
                :period="period"
                :config-checklist="configChecklist"
                :config-indicators="configIndicators"
            ></ActivityMonitorReports>
          </v-tab-item>
          <v-tab-item>
            <ActivityMonitorRatings
                :region-and-department-info="regionAndDepartmentInfo"
                :key="ratingKey"
                :period="period"
                :config-indicators="configIndicators"
            />
          </v-tab-item>
        </v-tabs>
      </div>
    </transition>
    <LoaderDialog :show="loading" msg="Загрузка..."></LoaderDialog>
  </div>
</template>

<script>
import RegionAndDepartmentPicker from "@/components/RegionAndDepartmentPicker";
import ModernizationWave from "@/components/elements/ModernizationWave";
import {getPeriodsByDomain} from "@/modules/api.periods";
import {periodStatusClass, periodStatusText} from "@/modules/periods";
import {getSnapshotDepartmentsByRegion} from "../../main";
import {getActiveDomainConfigs, getSnapshotDomainConfigs} from "../../modules/api.configs";
import LoaderDialog from "../elements/LoaderDialog.vue";
import ActivityMonitorCheckList from "./ActivityMonitorCheckList.vue";
import ActivityMonitorReports from "./ActivityMonitorReports.vue";
import ActivityMonitorRatings from "./ActivityMonitorRatings.vue";
import ActivityMonitorPlannedDialog from "./ActivityMonitorPlannedDialog.vue";
import {setDepartmentsByRegion} from "@/modules/ActivityMonitor";
import {FULL_ACCESS_ROLES, REGIONAL_ROLES, TERRITORIAL_ROLES} from "@/modules/ActivityMonitorRoles";
import {
  setActivityMonitorSettingsFromConfig,
  isRatingEnabled,
  isUserMonitorRegion,
  canUserAccessConfig,
  filterMonitorRegions,
} from "@/modules/ActivityMonitorRatingRegions";
import PassportInfoDocsUploadDialog from "../dialogs/PassportInfoDocsUploadDialog.vue";
import PassportInfoDocsDialog from "../dialogs/PassportInfoDocsDialog.vue";
import {ROLES} from "../../modules/constants";

const ONLY_SNAPSHOT_PERIODS = ['2026_H1_SEP'];

export default {
  name: 'ResultMonitorComponent',
  components: {
    PassportInfoDocsDialog,
    PassportInfoDocsUploadDialog,
    ActivityMonitorReports,
    ActivityMonitorRatings,
    ActivityMonitorPlannedDialog,
    LoaderDialog,
    RegionAndDepartmentPicker,
    ActivityMonitorCheckList,
    ModernizationWave
  },
  data: () => ({
    domain: 'activity_monitor',
    domainIndicators: 'activity_monitor_indicators',
    regionAndDepartmentInfo: {
      region: null,
      department: null,
    },
    checkListKey: 0,
    reportsKey: 0,
    ratingKey: 0,
    configChecklist: null,
    configIndicators: null,
    configFiles: null,
    periods: [],
    period: null,
    regions: null,
    loading: false,
    dialog: false,
    dialogUpload: false,
    dialogKey: 0,
    plannedDialogOpen: false,
    plannedDialogMode: 'region',
    initializingPeriods: false,
    ratingEnabled: false,
  }),
  methods: {
    periodStatusClass,
    periodStatusText,
    openPlannedDialog(mode) {
      this.plannedDialogMode = mode;
      this.plannedDialogOpen = true;
    },
    async resolveConfig(period, activeConfig) {
      if (period?.status === 'CLOSED' || ONLY_SNAPSHOT_PERIODS.includes(period?.code)) {
        const snapshotConfig = (await getSnapshotDomainConfigs(this.domain, period.code))?.payload?.content;
        return snapshotConfig ?? activeConfig;
      }
      return activeConfig;
    },
    async loadPeriods() {
      const allPeriods = (await getPeriodsByDomain(this.domain)).payload;
      if (!allPeriods?.length) return;

      this.initializingPeriods = true;
      this.loading = true;
      try {
        const activeConfig = (await getActiveDomainConfigs(this.domain)).payload?.[0]?.content;

        const periods = [];
        for (const period of allPeriods) {
          const config = await this.resolveConfig(period, activeConfig);
          if (canUserAccessConfig(this.$user, config)) {
            periods.push(period);
          }
        }

        this.periods = periods;
        this.period = periods[0] || null;

        if (this.period) {
          await this.loadConfig();
          await this.loadConfigIndicators();
          await this.loadRegions();
        }
      } finally {
        this.initializingPeriods = false;
        this.loading = false;
      }
    },
    async loadRegions() {
      const activeRegions = this.$cznDepartmentsByRegionList;
      const snapshotRegions = this.period?.status === 'CLOSED'
          ? await getSnapshotDepartmentsByRegion(this.domain, this.period)
          : null;

      const filterDepartments = (regions) => {
        if (!regions) return null;
        return regions.map(region => ({
          ...region,
          departments: region.departments
              ? region.departments.filter(department => department.type !== 'manager_czn')
              : []
        }));
      };

      const allRegions = filterDepartments(snapshotRegions) ?? filterDepartments(activeRegions);
      this.regions = filterMonitorRegions(allRegions);
      setDepartmentsByRegion(this.regions);
    },
    async loadConfig() {
      const activeConfig = (await getActiveDomainConfigs(this.domain)).payload?.[0]?.content;
      const config = await this.resolveConfig(this.period, activeConfig);

      this.configChecklist = config?.checklist;
      this.configFiles = config?.files;

      setActivityMonitorSettingsFromConfig(config);
      this.ratingEnabled = isRatingEnabled();
    },
    async loadConfigIndicators() {
      this.configIndicators = (await getActiveDomainConfigs(this.domainIndicators)).payload?.[0]?.content?.indicators;
    },
  },
  watch: {
    period: {
      async handler() {
        if (!this.period || this.initializingPeriods) return;
        this.loading = true;
        await this.loadConfig();
        await this.loadConfigIndicators();
        await this.loadRegions();
        this.loading = false;
      },
    },
  },
  computed: {
    ROLES() {
      return ROLES;
    },
    showRegionPlannedButton() {
      if (!this.ratingEnabled) return false;
      return FULL_ACCESS_ROLES.includes(this.$user.role)
          || (REGIONAL_ROLES.includes(this.$user.role) && isUserMonitorRegion(this.$user));
    },
    showCznPlannedButton() {
      if (!this.ratingEnabled) return false;
      if (FULL_ACCESS_ROLES.includes(this.$user.role)) return true;
      return (REGIONAL_ROLES.includes(this.$user.role) || TERRITORIAL_ROLES.includes(this.$user.role))
          && isUserMonitorRegion(this.$user);
    },
  },
  async beforeMount() {
    await this.loadPeriods();
  }
}
</script>

<style lang="scss">
@import "../../styles/main.scss";

.top-progress {
  margin-bottom: 30px;
  display: flex;
  align-items: center;

  > div > div {
    display: flex;
    flex-direction: column;
    height: 280px;
    border-radius: 20px;
    padding: 30px;

    div {
      font-weight: 600;
      font-size: 64px;
      line-height: 100%;
    }

    p {
      margin-top: auto;
      margin-bottom: 0;
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 140%;
      color: #1C1C1F;

    }
  }
}

.v-input--radio-group.v-input--radio-group--row {
  margin: 0;
}

.date-switch {
  margin-bottom: 60px;
  display: flex;
  justify-content: center;
  color: #0033A0;
}

.clientindex {
  .v-slide-group__content.v-tabs-bar__content {
    justify-content: stretch !important;

    .v-tab {
      flex: 1 1 50%;
      max-width: initial;
    }
  }

  .v-window-item {
    padding-top: 0px;
  }

  .v-expansion-panel .level2 {
    padding-top: 5px;

    .v-expansion-panel-content {
      padding-left: 44px;
    }
  }

  .v-expansion-panel-content p {
    font-weight: 500;
    font-size: 14px;
    line-height: 160%;
    color: #1C1C1F;
    margin: 10px 0;
  }

  .list .list-item-wrap .list-item-name {
    max-width: none;

    .toggleSwitch {
      align-self: center;
    }
  }

  .list .list-item-wrap .list-item {
    justify-content: flex-start;

    .v-progress-circular {
      margin-left: 0;

      .v-progress-circular__info {
        font-size: 16px;
        font-weight: 600;
        display: block;

        .text-caption {
          font-size: 10px !important;
          vertical-align: baseline;
          display: inline;
        }
      }
    }
  }

  .list-item-wrap .list-item-wrap {
    border: none;
    padding-bottom: 5px;
  }
}

.v-tabs {
  .v-tabs-bar {
    margin: 0 0 50px 0;
  }
}

.monitor-header {
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  gap: 20px;
  position: relative;

  h1 {
    flex: 1 1 100%;
  }
}

.monitor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  margin-bottom: 24px;

  &__period {
    flex: 0 1 400px;
    max-width: 100%;
    margin-bottom: 0;
  }

  &__planned {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-left: auto;
    min-height: 56px;
  }
}
</style>
